import { useCampaignContext } from 'context/CampaignContext';
import { useClientContext } from 'context/ClientContext';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTheme } from 'styled-components';

// import Highcharts from 'highcharts/highcharts-gantt';

// import CustomEvents from 'highcharts-custom-events';

// CustomEvents(Highcharts);

interface Chart {
  border: string;
  height: number;
  scrollablePlotArea: {
    minHeight: number;
    opacity: number;
  };
  marginRight: number;
  marginTop: number;
  backgroundColor: string;
  style: {
    fontFamily: string;
    fontWeight: string;
    color: string;
  };
  events?: {
    redraw?: (this: any) => void;
  };
}

interface XAxisEvent {
  afterSetExtremes: (this: any) => void;
}

interface XAxis {
  events?: XAxisEvent;
  grid: {
    cellHeight: number;
  };
  currentDateIndicator?: {
    width: number;
    color: string;
    dashStyle: string;
    label: {
      format: string;
    };
  };
}

interface YAxisLabelEvent {
  click: () => void;
}

interface YAxis {
  staticScale: number;
  forceRedraw: boolean;
  min?: number;
  // labels: {
  //   events?: YAxisLabelEvent;
  // };
  labels?: any;
}

interface Series {
  name: string | undefined;
  data: any[];
}

interface NavigatorSeries {
  type: string;
  pointPlacement: number;
  pointPadding: number;
  accessibility: {
    enabled: boolean;
  };
}

interface NavigatorYAxis {
  min: number;
  max: number;
  reversed: boolean;
  categories: any[];
}

interface Navigator {
  enabled: boolean;
  liveRedraw: boolean;
  series: NavigatorSeries;
  yAxis: NavigatorYAxis;
}

export interface TestType {
  chart: Chart;
  credits: {
    enabled: boolean;
  };
  xAxis: XAxis[];
  yAxis: YAxis[];
  plotOptions?: {
    series?: {
      animation?: boolean;
      events?: {
        afterAnimate?: (this: any) => void;
      };
    };
    gantt?: {
      point?: {
        events?: {
          click?: (this: any) => void;
        };
      };
    };
  };
  scrollbar: {
    enabled: boolean;
  };
  rangeSelector: {
    enabled: boolean;
  };
  series: Series[];
  navigator: Navigator;
}

export const useGetGanttOptions = (
  seriesArray: any[] | null,
  // height: number,
  // startDate: number | undefined,
  // endDate: number | undefined,
): readonly [TestType | null] => {
  const { activeOrganization } = useClientContext();
  const { campaignList } = useCampaignContext();
  const theme = useTheme();
  const [options, setOptions] = useState<any | null>(null);

  const checkIfCampaignisUmbrellaWithoutChildren = useCallback(
    (campaignNameClicked: string) => {
      const campaignClicked = campaignList?.find((campaign) => campaign?.campaign === campaignNameClicked);

      if (campaignClicked) {
        const isUmbrellaWithoutChildren =
          campaignClicked?.campaignId.toString().startsWith('100') && !campaignClicked?.childCampaigns?.length;
        if (isUmbrellaWithoutChildren) {
          // console.log(campaignClicked);
          return campaignClicked.campaignId;
        } else {
          return null;
        }
      } else {
        return null;
      }
    },
    [campaignList],
  );

  useEffect(() => {
    // Process immediately without timeout to avoid race conditions
    if (seriesArray !== null && seriesArray.length > 0) {
      // Count number of parent groups (items without 'parent' property)
      const parentCount = seriesArray.filter((item: any) => !item.parent).length;

      // Ensure minimum height for small datasets
      const calculatedHeight = Math.max((seriesArray.length + 3) * 31, 800);
      const chartHeight = calculatedHeight < 800 ? calculatedHeight : 800;
      const minHeight = calculatedHeight < 800 ? calculatedHeight : (seriesArray.length + 3) * 31;

      // For single group, use exact height to prevent rows from stretching
      const chartConfig: any = {
        border: theme.border,
        height: chartHeight, // Use same height logic for both cases
        marginRight: 30,
        marginTop: 90,
        backgroundColor: theme.placeholderBackground,
        style: {
          fontFamily: 'fontRegular',
          fontWeight: 'normal',
          color: theme.primaryGray,
        },
      };

      // Add scrollablePlotArea for both cases, but with exact minHeight for single group
      // to prevent rows from stretching while keeping collapse/expand working

      if (parentCount > 1) {
        chartConfig.scrollablePlotArea = {
          minHeight: minHeight,
          opacity: 1,
        };
      } else {
        // For single group, use exact calculated height to prevent stretching
        chartConfig.scrollablePlotArea = {
          minHeight: calculatedHeight,
          opacity: 1,
        };
      }

      const test: TestType = {
        chart: chartConfig,
        credits: {
          enabled: false,
        },
        xAxis: [
          {
            events: {
              afterSetExtremes: function (this: any) {
                if (this.chart.xAxis[1].max - this.chart.xAxis[1].min < 17280000000) {
                  this.update({
                    tickInterval: 36e7,
                  });
                } else {
                  this.update({
                    tickInterval: 2592000000,
                  });
                  this.chart.xAxis[1].update({
                    tickInterval: undefined,
                  });
                }
              },
            },
            grid: {
              cellHeight: 30,
            },
            // min: startDate,
            // max: endDate,
            currentDateIndicator: {
              width: 4,
              color: theme.brand,
              dashStyle: 'dot',
              label: {
                format: '%A, %e %B',
              },
            },
          },
          {
            grid: {
              cellHeight: 30,
            },
          },
        ],
        yAxis: [
          {
            staticScale: 30,
            forceRedraw: true,
            labels: {
              formatter: function (this: any) {
                const hasIcon = checkIfCampaignisUmbrellaWithoutChildren(this.value);
                if (hasIcon) {
                  return `<div style='color:${theme.c300}'>${this.value}
                      <span  style='color:${theme.c300}'>(no child campaigns)</span>
                    </div>`;
                } else {
                  return this.value;
                }
              },
              // style: {
              //   fontSize: '8px',
              //   width: '150px',
              // },
              // events: {
              //   click: function (this: any) {
              //     console.log('this', this);
              //     const splitedValue = this.value.split('<span')[0].replace('<div>', '').trim();
              //     const hasIcon = checkIfCampaignisUmbrellaWithoutChildren(splitedValue);
              //     console.log('hasIcon', hasIcon);
              //     hasIcon && deleteUmbrella(hasIcon);
              //   },
              // },
            },
          },
        ],

        scrollbar: {
          enabled: false,
        },
        rangeSelector: {
          enabled: false,
        },
        series: [
          {
            name: activeOrganization?.Organization,
            data: seriesArray,
          },
        ],

        navigator: {
          enabled: false,
          liveRedraw: true,
          series: {
            type: 'gantt',
            pointPlacement: 0.5,
            pointPadding: 0.25,
            accessibility: {
              enabled: false,
            },
          },
          yAxis: {
            min: 0,
            max: 3,
            reversed: true,
            categories: [],
          },
        },
      };
      setOptions(test);
    } else {
      setOptions(null);
    }
  }, [
    activeOrganization?.Organization,
    seriesArray,
    theme.border,
    theme.brand,
    theme.placeholderBackground,
    theme.primaryGray,
    theme.c300,
    checkIfCampaignisUmbrellaWithoutChildren,
  ]); // useEffect(() => {
  //   if (activeOrganization?.Organization !== undefined && seriesArray !== null) {
  //     setOptions({
  //       chart: {
  //         height: seriesArray.length * 30 < 800 ? seriesArray.length * 31 : 800,
  //         scrollablePlotArea: {
  //           minHeight: seriesArray.length * 31,
  //           opacity: 1,
  //         },
  //         marginRight: 30,
  //         marginTop: 90,
  //         // backgroundColor: theme.background,
  //         style: {
  //           fontFamily: 'fontRegular',
  //           fontWeight: 'normal',
  //         },

  //         events: {
  //           load(chart: any) {},
  //         },
  //       },
  //       series: [
  //         {
  //           name: activeOrganization?.Organization,
  //           data: seriesArray,
  //         },
  //       ],
  //     });
  //   }
  // }, [activeOrganization?.Organization, seriesArray]);

  return [options];
};
